import type { CurrencyRate, ExceptionPolicyRule, HotelPolicyDataConfig, MileagePolicyRule, PolicyBaseCurrency, ReceiptPolicyRule } from "@app/types/policy";
import { LOST_RECEIPT_ESCALATION_LEVEL, POLICY_AUTOMATIC, POLICY_FIELD_REQUIREMENT, POLICY_STATUS } from "@app/types/policy";

export const MOCK_EXCEPTION_POLICY_RULES: ExceptionPolicyRule[] = [
    {
        id: "exc-1",
        ruleName: "Executive Airfare Override",
        status: POLICY_STATUS.Active,
        category: "Airfare",
        expression: "Greater than",
        amount: "$5,000",
        entryType: "Manual",
        response: "Approve",
        categoryInactive: false,
    },
    {
        id: "exc-2",
        ruleName: "Software Subscription Limit",
        status: POLICY_STATUS.Active,
        category: "Software",
        expression: "Greater than",
        amount: "$2,500",
        entryType: "Corp card",
        response: "Notify",
        categoryInactive: false,
    },
    {
        id: "exc-3",
        ruleName: "Employee Meal Per Diem",
        status: POLICY_STATUS.Active,
        category: "Employee meal",
        expression: "Between",
        amount: "$15 - $75",
        entryType: "Manual",
        response: "Approve",
        categoryInactive: false,
    },
    {
        id: "exc-4",
        ruleName: "Car Rental Weekend Block",
        status: POLICY_STATUS.Active,
        category: "Car rental",
        expression: "Greater than",
        amount: "$300",
        entryType: "Travel feed",
        response: "Block",
        categoryInactive: false,
    },
    {
        id: "exc-5",
        ruleName: "Hotel Weekend Premium",
        status: POLICY_STATUS.Active,
        category: "Other",
        expression: "Greater than",
        amount: "$3,500",
        entryType: "Manual",
        response: "Notify",
        categoryInactive: false,
    },
    {
        id: "exc-6",
        ruleName: "All Categories Daily Cap",
        status: POLICY_STATUS.Active,
        category: "All",
        expression: "Greater than",
        amount: "$500",
        entryType: "Corp card",
        response: "Explain",
        categoryInactive: false,
    },
    {
        id: "exc-7",
        ruleName: "Corporate Card Mismatch",
        status: POLICY_STATUS.Active,
        category: "All",
        expression: "Greater than",
        amount: "$100",
        entryType: "Corp card",
        response: "Block",
        categoryInactive: false,
    },
    {
        id: "exc-8",
        ruleName: "International Lodging Cap",
        status: POLICY_STATUS.Active,
        category: "Lodging",
        expression: "Greater than",
        amount: "$450",
        entryType: "Travel feed",
        response: "Notify",
        categoryInactive: false,
    },
    {
        id: "exc-9",
        ruleName: "Parking Reimbursement Limit",
        status: POLICY_STATUS.Deactivated,
        category: "Parking",
        expression: "Greater than",
        amount: "$75",
        entryType: "Manual",
        response: "Approve",
        categoryInactive: false,
    },
    {
        id: "exc-10",
        ruleName: "Client Entertainment Threshold",
        status: POLICY_STATUS.Active,
        category: "Entertainment",
        expression: "Greater than",
        amount: "$1,200",
        entryType: "Manual",
        response: "Explain",
        categoryInactive: false,
    },
    {
        id: "exc-11",
        ruleName: "Ride Share Commute Block",
        status: POLICY_STATUS.Active,
        category: "Ground transport",
        expression: "Between",
        amount: "$20 - $80",
        entryType: "Corp card",
        response: "Block",
        categoryInactive: false,
    },
    {
        id: "exc-12",
        ruleName: "Conference Registration Cap",
        status: POLICY_STATUS.Active,
        category: "Training",
        expression: "Greater than",
        amount: "$2,000",
        entryType: "Manual",
        response: "Notify",
        categoryInactive: false,
    },
    {
        id: "exc-13",
        ruleName: "Office Supplies Auto-Approve",
        status: POLICY_STATUS.Active,
        category: "Supplies",
        expression: "Less than",
        amount: "$150",
        entryType: "Corp card",
        response: "Approve",
        categoryInactive: false,
    },
    {
        id: "exc-14",
        ruleName: "Late Submission Penalty",
        status: POLICY_STATUS.Deactivated,
        category: "All",
        expression: "Greater than",
        amount: "$0",
        entryType: "Manual",
        response: "Explain",
        categoryInactive: false,
    },
    {
        id: "exc-15",
        ruleName: "Duplicate Receipt Detection",
        status: POLICY_STATUS.Active,
        category: "All",
        expression: "Equal to",
        amount: "$0",
        entryType: "Travel feed",
        response: "Block",
        categoryInactive: false,
    },
];

const USD_BASE_RATES: CurrencyRate[] = [
    { id: "cur-1", currency: "Euro", currencyCode: "EUR", exchangeRate: "0.9200" },
    { id: "cur-2", currency: "British Pound", currencyCode: "GBP", exchangeRate: "0.7900" },
    { id: "cur-3", currency: "Japanese Yen", currencyCode: "JPY", exchangeRate: "149.8500" },
    { id: "cur-4", currency: "Canadian Dollar", currencyCode: "CAD", exchangeRate: "1.3600" },
    { id: "cur-5", currency: "Australian Dollar", currencyCode: "AUD", exchangeRate: "1.5200" },
    { id: "cur-6", currency: "Swiss Franc", currencyCode: "CHF", exchangeRate: "0.8800" },
    { id: "cur-7", currency: "Chinese Yuan", currencyCode: "CNY", exchangeRate: "7.2400" },
    { id: "cur-8", currency: "Mexican Peso", currencyCode: "MXN", exchangeRate: "17.1200" },
    { id: "cur-9", currency: "Singapore Dollar", currencyCode: "SGD", exchangeRate: "1.3400" },
    { id: "cur-10", currency: "Hong Kong Dollar", currencyCode: "HKD", exchangeRate: "7.8200" },
    { id: "cur-11", currency: "New Zealand Dollar", currencyCode: "NZD", exchangeRate: "1.6400" },
    { id: "cur-12", currency: "Swedish Krona", currencyCode: "SEK", exchangeRate: "10.4500" },
    { id: "cur-13", currency: "Norwegian Krone", currencyCode: "NOK", exchangeRate: "10.6800" },
    { id: "cur-14", currency: "Indian Rupee", currencyCode: "INR", exchangeRate: "83.2500" },
    { id: "cur-15", currency: "South Korean Won", currencyCode: "KRW", exchangeRate: "1350.5000" },
    { id: "cur-16", currency: "Brazilian Real", currencyCode: "BRL", exchangeRate: "5.1200" },
];

const CAD_BASE_RATES: CurrencyRate[] = [
    { id: "cur-1", currency: "Euro", currencyCode: "EUR", exchangeRate: "0.6760" },
    { id: "cur-2", currency: "British Pound", currencyCode: "GBP", exchangeRate: "0.5810" },
    { id: "cur-3", currency: "Japanese Yen", currencyCode: "JPY", exchangeRate: "110.1800" },
    { id: "cur-4", currency: "US Dollar", currencyCode: "USD", exchangeRate: "0.7350" },
    { id: "cur-5", currency: "Australian Dollar", currencyCode: "AUD", exchangeRate: "1.1180" },
    { id: "cur-6", currency: "Swiss Franc", currencyCode: "CHF", exchangeRate: "0.6470" },
];

const EUR_BASE_RATES: CurrencyRate[] = [
    { id: "cur-1", currency: "US Dollar", currencyCode: "USD", exchangeRate: "1.0870" },
    { id: "cur-2", currency: "British Pound", currencyCode: "GBP", exchangeRate: "0.8580" },
    { id: "cur-3", currency: "Japanese Yen", currencyCode: "JPY", exchangeRate: "162.8800" },
    { id: "cur-4", currency: "Canadian Dollar", currencyCode: "CAD", exchangeRate: "1.4780" },
    { id: "cur-5", currency: "Australian Dollar", currencyCode: "AUD", exchangeRate: "1.6520" },
    { id: "cur-6", currency: "Swiss Franc", currencyCode: "CHF", exchangeRate: "0.9560" },
];

const GBP_BASE_RATES: CurrencyRate[] = [
    { id: "cur-1", currency: "Euro", currencyCode: "EUR", exchangeRate: "1.1660" },
    { id: "cur-2", currency: "US Dollar", currencyCode: "USD", exchangeRate: "1.2660" },
    { id: "cur-3", currency: "Japanese Yen", currencyCode: "JPY", exchangeRate: "189.6800" },
    { id: "cur-4", currency: "Canadian Dollar", currencyCode: "CAD", exchangeRate: "1.7220" },
    { id: "cur-5", currency: "Australian Dollar", currencyCode: "AUD", exchangeRate: "1.9240" },
    { id: "cur-6", currency: "Swiss Franc", currencyCode: "CHF", exchangeRate: "1.1140" },
];

const JPY_BASE_RATES: CurrencyRate[] = [
    { id: "cur-1", currency: "US Dollar", currencyCode: "USD", exchangeRate: "0.0067" },
    { id: "cur-2", currency: "Euro", currencyCode: "EUR", exchangeRate: "0.0061" },
    { id: "cur-3", currency: "British Pound", currencyCode: "GBP", exchangeRate: "0.0053" },
    { id: "cur-4", currency: "Canadian Dollar", currencyCode: "CAD", exchangeRate: "0.0091" },
    { id: "cur-5", currency: "Australian Dollar", currencyCode: "AUD", exchangeRate: "0.0101" },
    { id: "cur-6", currency: "Swiss Franc", currencyCode: "CHF", exchangeRate: "0.0059" },
];

const AUD_BASE_RATES: CurrencyRate[] = [
    { id: "cur-1", currency: "US Dollar", currencyCode: "USD", exchangeRate: "0.6579" },
    { id: "cur-2", currency: "Euro", currencyCode: "EUR", exchangeRate: "0.6053" },
    { id: "cur-3", currency: "British Pound", currencyCode: "GBP", exchangeRate: "0.5197" },
    { id: "cur-4", currency: "Japanese Yen", currencyCode: "JPY", exchangeRate: "98.5855" },
    { id: "cur-5", currency: "Canadian Dollar", currencyCode: "CAD", exchangeRate: "0.8947" },
    { id: "cur-6", currency: "Swiss Franc", currencyCode: "CHF", exchangeRate: "0.5789" },
];

const CHF_BASE_RATES: CurrencyRate[] = [
    { id: "cur-1", currency: "US Dollar", currencyCode: "USD", exchangeRate: "1.1364" },
    { id: "cur-2", currency: "Euro", currencyCode: "EUR", exchangeRate: "1.0455" },
    { id: "cur-3", currency: "British Pound", currencyCode: "GBP", exchangeRate: "0.8977" },
    { id: "cur-4", currency: "Japanese Yen", currencyCode: "JPY", exchangeRate: "170.2841" },
    { id: "cur-5", currency: "Canadian Dollar", currencyCode: "CAD", exchangeRate: "1.5455" },
    { id: "cur-6", currency: "Australian Dollar", currencyCode: "AUD", exchangeRate: "1.7273" },
];

const RATES_BY_BASE: Record<PolicyBaseCurrency, CurrencyRate[]> = {
    USD: USD_BASE_RATES,
    EUR: EUR_BASE_RATES,
    GBP: GBP_BASE_RATES,
    JPY: JPY_BASE_RATES,
    CAD: CAD_BASE_RATES,
    AUD: AUD_BASE_RATES,
    CHF: CHF_BASE_RATES,
};

const DATE_RATE_MULTIPLIERS: Record<string, number> = {
    "2026-06-16": 1,
    "2026-06-17": 1.004,
    "2026-06-18": 0.998,
};

function scaleExchangeRate(rate: string, multiplier: number): string {
    const value = Number.parseFloat(rate);
    if (!Number.isFinite(value)) return rate;
    return (value * multiplier).toFixed(4);
}

export function getMockCurrencyRates(baseCurrency: PolicyBaseCurrency, dateKey: string): CurrencyRate[] {
    const baseRates = RATES_BY_BASE[baseCurrency];
    const multiplier = DATE_RATE_MULTIPLIERS[dateKey] ?? 1;

    return baseRates.map((rate) => ({
        ...rate,
        exchangeRate: scaleExchangeRate(rate.exchangeRate, multiplier),
    }));
}

export const DEFAULT_HOTEL_POLICY_CONFIG: HotelPolicyDataConfig = {
    recommendedFields: [
        {
            id: "total-amount",
            label: "Total Amount",
            enabled: true,
            fieldType: "Currency",
            requirement: POLICY_FIELD_REQUIREMENT.Mandatory,
            description: "",
        },
        {
            id: "room-rate",
            label: "Room Rate (Accommodation)",
            enabled: true,
            fieldType: "Currency",
            requirement: POLICY_FIELD_REQUIREMENT.Mandatory,
            description: "",
        },
        {
            id: "hotel-tax",
            label: "Hotel / Occupancy Tax",
            enabled: true,
            fieldType: "Currency",
            requirement: POLICY_FIELD_REQUIREMENT.Mandatory,
            description: "",
        },
    ],
    additionalFields: [
        {
            id: "resort-fee",
            label: "Resort / Destination Fee",
            enabled: false,
            fieldType: "Currency",
            requirement: POLICY_FIELD_REQUIREMENT.Optional,
            description: "",
        },
        {
            id: "meals",
            label: "Meals / Food & Beverage",
            enabled: false,
            fieldType: "Currency",
            requirement: POLICY_FIELD_REQUIREMENT.Optional,
            description: "",
        },
        {
            id: "parking",
            label: "Parking",
            enabled: false,
            fieldType: "Currency",
            requirement: POLICY_FIELD_REQUIREMENT.Optional,
            description: "",
        },
        {
            id: "other",
            label: "Other / Incidentals",
            enabled: false,
            fieldType: "Currency",
            requirement: POLICY_FIELD_REQUIREMENT.Optional,
            description: "",
        },
    ],
    personalNonReimbursable: {
        id: "personal",
        label: "Personal / Non-Reimbursable - Collect with each category",
        enabled: true,
        fieldType: "Toggle + Amount",
        requirement: POLICY_FIELD_REQUIREMENT.StronglyRecommended,
        description: "",
    },
    outsideHomeCountry: false,
    vatFields: [
        {
            id: "vat-amount",
            label: "VAT Amount",
            enabled: false,
            fieldType: "Currency",
            requirement: POLICY_FIELD_REQUIREMENT.Optional,
            description: "",
        },
        {
            id: "vat-tax-id",
            label: "Hotel VAT Tax ID",
            enabled: false,
            fieldType: "Text / OCR-confirmed",
            requirement: POLICY_FIELD_REQUIREMENT.Optional,
            description: "",
        },
    ],
};

export const MOCK_RECEIPT_POLICY_RULES: ReceiptPolicyRule[] = [
    {
        id: "rcp-1",
        ruleName: "Executive Travel",
        status: POLICY_STATUS.Active,
        requiredAmount: "$50.00",
        lostProtocol: "Expense Not Reimbursed",
        automatic: POLICY_AUTOMATIC.Yes,
        updatedBy: "Michael Chen",
        changedDate: "Feb 20, 2026",
        requireReceiptAtOrAbove: "$50",
        retainReceiptsFor: "3 Years",
        allowMultipleImageUploads: true,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Initial,
    },
    {
        id: "rcp-2",
        ruleName: "Petty Cash",
        status: POLICY_STATUS.Deactivated,
        requiredAmount: "$0.01",
        lostProtocol: "Notification Only",
        automatic: POLICY_AUTOMATIC.No,
        updatedBy: "Jennifer Smith",
        changedDate: "Jan 10, 2026",
        requireReceiptAtOrAbove: "$25",
        retainReceiptsFor: "1 Year",
        allowMultipleImageUploads: false,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Off,
    },
    {
        id: "rcp-3",
        ruleName: "Standard Receipt",
        status: POLICY_STATUS.Active,
        requiredAmount: "$25.00",
        lostProtocol: "Manager Approval Required",
        automatic: POLICY_AUTOMATIC.Yes,
        updatedBy: "Crystal Davis",
        changedDate: "Feb 15, 2026",
        requireReceiptAtOrAbove: "$25",
        retainReceiptsFor: "6 Months",
        allowMultipleImageUploads: true,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Explanation,
    },
    {
        id: "rcp-4",
        ruleName: "International Travel",
        status: POLICY_STATUS.Active,
        requiredAmount: "$75.00",
        lostProtocol: "Expense Not Reimbursed",
        automatic: POLICY_AUTOMATIC.Yes,
        updatedBy: "Michael Chen",
        changedDate: "Mar 1, 2026",
        requireReceiptAtOrAbove: "$75",
        retainReceiptsFor: "7 Years",
        allowMultipleImageUploads: true,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Initial,
    },
    {
        id: "rcp-5",
        ruleName: "Client Meals",
        status: POLICY_STATUS.Active,
        requiredAmount: "$100.00",
        lostProtocol: "Manager Approval Required",
        automatic: POLICY_AUTOMATIC.No,
        updatedBy: "Crystal Davis",
        changedDate: "Mar 5, 2026",
        requireReceiptAtOrAbove: "$100",
        retainReceiptsFor: "3 Years",
        allowMultipleImageUploads: false,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Off,
    },
    {
        id: "rcp-6",
        ruleName: "Ground Transportation",
        status: POLICY_STATUS.Active,
        requiredAmount: "$15.00",
        lostProtocol: "Notification Only",
        automatic: POLICY_AUTOMATIC.Yes,
        updatedBy: "Jennifer Smith",
        changedDate: "Mar 8, 2026",
        requireReceiptAtOrAbove: "$15",
        retainReceiptsFor: "1 Year",
        allowMultipleImageUploads: true,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Off,
    },
    {
        id: "rcp-7",
        ruleName: "Office Supplies",
        status: POLICY_STATUS.Active,
        requiredAmount: "$10.00",
        lostProtocol: "Notification Only",
        automatic: POLICY_AUTOMATIC.Yes,
        updatedBy: "Michael Chen",
        changedDate: "Mar 10, 2026",
        requireReceiptAtOrAbove: "$10",
        retainReceiptsFor: "6 Months",
        allowMultipleImageUploads: false,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Off,
    },
    {
        id: "rcp-8",
        ruleName: "Conference Registration",
        status: POLICY_STATUS.Active,
        requiredAmount: "$200.00",
        lostProtocol: "Expense Not Reimbursed",
        automatic: POLICY_AUTOMATIC.No,
        updatedBy: "Crystal Davis",
        changedDate: "Mar 12, 2026",
        requireReceiptAtOrAbove: "$200",
        retainReceiptsFor: "Indefinitely",
        allowMultipleImageUploads: true,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Explanation,
    },
    {
        id: "rcp-9",
        ruleName: "Remote Work Stipend",
        status: POLICY_STATUS.Deactivated,
        requiredAmount: "$0.01",
        lostProtocol: "Notification Only",
        automatic: POLICY_AUTOMATIC.No,
        updatedBy: "Jennifer Smith",
        changedDate: "Feb 28, 2026",
        requireReceiptAtOrAbove: "$0",
        retainReceiptsFor: "1 Year",
        allowMultipleImageUploads: false,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Off,
    },
    {
        id: "rcp-10",
        ruleName: "Team Events",
        status: POLICY_STATUS.Active,
        requiredAmount: "$35.00",
        lostProtocol: "Manager Approval Required",
        automatic: POLICY_AUTOMATIC.Yes,
        updatedBy: "Michael Chen",
        changedDate: "Mar 15, 2026",
        requireReceiptAtOrAbove: "$35",
        retainReceiptsFor: "3 Years",
        allowMultipleImageUploads: true,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Initial,
    },
    {
        id: "rcp-11",
        ruleName: "Parking Reimbursement",
        status: POLICY_STATUS.Active,
        requiredAmount: "$20.00",
        lostProtocol: "Notification Only",
        automatic: POLICY_AUTOMATIC.Yes,
        updatedBy: "Crystal Davis",
        changedDate: "Mar 18, 2026",
        requireReceiptAtOrAbove: "$20",
        retainReceiptsFor: "6 Months",
        allowMultipleImageUploads: true,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Off,
    },
    {
        id: "rcp-12",
        ruleName: "Training Materials",
        status: POLICY_STATUS.Active,
        requiredAmount: "$40.00",
        lostProtocol: "Expense Not Reimbursed",
        automatic: POLICY_AUTOMATIC.No,
        updatedBy: "Jennifer Smith",
        changedDate: "Mar 20, 2026",
        requireReceiptAtOrAbove: "$40",
        retainReceiptsFor: "7 Years",
        allowMultipleImageUploads: false,
        lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Off,
    },
];

export const MOCK_MILEAGE_POLICY_RULES: MileagePolicyRule[] = [
    {
        id: "mil-1",
        ruleName: "Standard Business Mileage",
        status: POLICY_STATUS.Active,
        rate: "$0.67",
        unit: "per mile",
        updatedBy: "Crystal Davis",
        changedDate: "Jan 5, 2026",
    },
    {
        id: "mil-2",
        ruleName: "Executive Mileage Rate",
        status: POLICY_STATUS.Active,
        rate: "$0.72",
        unit: "per mile",
        updatedBy: "Michael Chen",
        changedDate: "Jan 12, 2026",
    },
    {
        id: "mil-3",
        ruleName: "Canada Kilometer Rate",
        status: POLICY_STATUS.Active,
        rate: "$0.68",
        unit: "per km",
        updatedBy: "Jennifer Smith",
        changedDate: "Feb 1, 2026",
    },
    {
        id: "mil-4",
        ruleName: "Field Sales Mileage",
        status: POLICY_STATUS.Active,
        rate: "$0.70",
        unit: "per mile",
        updatedBy: "Crystal Davis",
        changedDate: "Feb 8, 2026",
    },
    {
        id: "mil-5",
        ruleName: "Volunteer Mileage",
        status: POLICY_STATUS.Deactivated,
        rate: "$0.14",
        unit: "per mile",
        updatedBy: "Michael Chen",
        changedDate: "Feb 10, 2026",
    },
    {
        id: "mil-6",
        ruleName: "Airport Transfer Mileage",
        status: POLICY_STATUS.Active,
        rate: "$0.67",
        unit: "per mile",
        updatedBy: "Jennifer Smith",
        changedDate: "Feb 14, 2026",
    },
    {
        id: "mil-7",
        ruleName: "Relocation Mileage",
        status: POLICY_STATUS.Active,
        rate: "$0.22",
        unit: "per mile",
        updatedBy: "Crystal Davis",
        changedDate: "Feb 18, 2026",
    },
    {
        id: "mil-8",
        ruleName: "Medical Appointment Mileage",
        status: POLICY_STATUS.Active,
        rate: "$0.21",
        unit: "per mile",
        updatedBy: "Michael Chen",
        changedDate: "Feb 22, 2026",
    },
    {
        id: "mil-9",
        ruleName: "Charitable Mileage",
        status: POLICY_STATUS.Deactivated,
        rate: "$0.14",
        unit: "per mile",
        updatedBy: "Jennifer Smith",
        changedDate: "Mar 1, 2026",
    },
    {
        id: "mil-10",
        ruleName: "Construction Site Mileage",
        status: POLICY_STATUS.Active,
        rate: "$0.69",
        unit: "per mile",
        updatedBy: "Crystal Davis",
        changedDate: "Mar 4, 2026",
    },
    {
        id: "mil-11",
        ruleName: "UK Mileage Rate",
        status: POLICY_STATUS.Active,
        rate: "£0.45",
        unit: "per mile",
        updatedBy: "Michael Chen",
        changedDate: "Mar 8, 2026",
    },
    {
        id: "mil-12",
        ruleName: "EU Kilometer Rate",
        status: POLICY_STATUS.Active,
        rate: "€0.30",
        unit: "per km",
        updatedBy: "Jennifer Smith",
        changedDate: "Mar 12, 2026",
    },
];
